import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react';
import { BehaviorSubject, Observable } from 'rxjs';
import type { WindowProps, WindowPayload, WindowType } from './window.types';

type WindowContextValue = {
  windows$: Observable<Map<WindowType, WindowPayload>>;
  openWindow: (windowProps: WindowProps) => void;
  closeWindow: (type: WindowType) => void;
};

const WindowContext = createContext<WindowContextValue | undefined>(undefined);

const scrubPayload = (payload: WindowPayload | undefined) => {
  if (!payload) return;
  payload.windowProps = undefined;
};

export const WindowStateProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const windowsSubject$ = useMemo(
    () => new BehaviorSubject<Map<WindowType, WindowPayload>>(new Map()),
    []
  );

  const openWindow = useCallback(
    (windowProps: WindowProps) => {
      const currentWindows = windowsSubject$.getValue();
      const updatedWindows = new Map(currentWindows);
      updatedWindows.set(windowProps.type, { open: true, windowProps });
      windowsSubject$.next(updatedWindows);
    },
    [windowsSubject$]
  );

  const closeWindow = useCallback(
    (type: WindowType) => {
      const currentWindows = windowsSubject$.getValue();
      const payload = currentWindows.get(type);

      if (payload?.windowProps?.type === 'TradingForm') {
        payload?.windowProps?.componentProps?.onFormDestroy?.();
      }

      // Proactively break references held by stale snapshots of window maps.
      scrubPayload(payload);
      currentWindows.delete(type);

      windowsSubject$.next(new Map(currentWindows));
    },
    [windowsSubject$]
  );

  useEffect(() => {
    return () => {
      const currentWindows = windowsSubject$.getValue();
      currentWindows.forEach((payload) => scrubPayload(payload));
      currentWindows.clear();
      windowsSubject$.next(new Map());
      windowsSubject$.complete();
    };
  }, [windowsSubject$]);

  const value = useMemo<WindowContextValue>(
    () => ({
      windows$: windowsSubject$.asObservable(),
      openWindow,
      closeWindow
    }),
    [closeWindow, openWindow, windowsSubject$]
  );

  return React.createElement(WindowContext.Provider, { value }, children);
};

export const useWindow = () => {
  const context = useContext(WindowContext);
  if (!context) {
    throw new Error('useWindow must be used within WindowStateProvider');
  }

  return context;
};
